import type {Component, Editor, StyleProps} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES, UNSUPPORTED_EMAIL_STYLES} from '@EmailMakerReact/email-maker-react.constants';
import type {StyleAttributes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getAllComponentsInTree, getMainWrapper, getTableComponentForButton, hasWidthProperty} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {addAlignAttribute, setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {doesTableRequireTbody, updateLegacyDividers} from '@EmailMakerReact/lib/email-maker-table-library';
import {saveEmailTemplateToCache} from '@EmailMakerReact/lib/email-maker-storage-library';

/** Outlook ignores CSS width on images; the width attribute is normalized against this mailbox width. */
const MAX_MAILBOX_WIDTH = 700;
/** Fallback document width used when the canvas element has no offsetWidth yet. */
const DEFAULT_DOCUMENT_WIDTH_PX = 1;

const REDUNDANT_COMPONENT_TYPES: readonly string[] = ['postermywall-drag-plh'];

/** Mirrors the rendered width into a width attribute so Outlook does not blow images up to their intrinsic size. */
export function updateMsOutlookStyles(editor: Editor, component: Component | undefined): void {
  if (!component || !hasWidthProperty(component) || !component.view) {
    return;
  }

  const width = getNormalizedWidth(editor, component.view.el.offsetWidth);
  if (!width) {
    return;
  }

  component.removeAttributes(['width', 'height'], {silent: true});
  component.addAttributes({width}, {silent: true});
}

/** Per-component default-prop, Outlook-width, redundant-component and legacy-divider fixes across the whole template. */
export function setLegacyMailCompatibility(editor: Editor): void {
  for (const component of getAllEditorComponents(editor)) {
    fixComponentDefaultProps(component);
    updateMsOutlookStyles(editor, component);
    deleteRedundantComponents(component);
    updateLegacyDividers(component);
  }
}

/** Syncs the background attribute/style pair on every table component for Gmail. */
export function updateBgImageGmailCompatibility(editor: Editor): void {
  for (const component of getAllEditorComponents(editor)) {
    replaceBgImageAttributeWithStyle(component);
  }
}

/** Mirrors a table's background-image style into the background attribute (and vice versa). */
export function replaceBgImageAttributeWithStyle(component: Component | undefined): void {
  if (!component) {
    return;
  }

  const style = component.get('style') as StyleProps | undefined;
  const attributes = component.get('attributes') as StyleAttributes | undefined;

  const backgroundImageStyle = style?.['background-image'];
  if (backgroundImageStyle) {
    const url = /url\((.*?)\)/i.exec(getStyleValueAsString(backgroundImageStyle));
    if (url?.[1] && isTableTagComponent(component)) {
      component.addAttributes({
        background: url[1],
      });
    }
  } else if (attributes?.background) {
    const url = attributes.background;
    if (url && isTableTagComponent(component)) {
      component.addStyle({
        'background-image': `url(${url})`,
      });
    }
  }
}

/** Rebuilds any PMW button whose content is not table-wrapped, for AOL. */
export function updateButtonAolCompatibility(editor: Editor): void {
  for (const component of getAllEditorComponents(editor)) {
    if (component.get('type') !== EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON) {
      continue;
    }

    if (component.getChildAt(0)?.get('tagName') === 'table') {
      continue;
    }

    component.components([getTableComponentForButton(component.getInnerHTML())]);
  }
}

/** Wraps tbody-less tables/sections in a PMW tbody for Outlook. This rewrites serialized output. */
export function updateTableOutlookCompatibility(editor: Editor): void {
  for (const component of getAllEditorComponents(editor)) {
    if (!doesTableRequireTbody(component)) {
      continue;
    }

    const child = component.getChildAt(0);
    // Skip child-less tables rather than wrap a runtime-undefined child.
    if (!child) {
      continue;
    }

    component.components({type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY, components: [child]});
  }
}

/** Full-width buttons/social blocks overflow on AOL/Yahoo; forces block display with unset width. */
export function fixComponentFullWidthOverflowAolYahoo(editor: Editor): void {
  const types: string[] = [EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON, EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS];

  for (const component of getAllEditorComponents(editor, types)) {
    const styles: StyleProps = component.getStyle();
    if (styles.width === '100%' || (styles.display === 'table' && styles.width === 'unset')) {
      setComponentStyle(editor, {property: 'display', value: 'block', component});
      setComponentStyle(editor, {property: 'width', value: 'unset', component});
    }
  }
}

/** Strips style properties email clients don't support (component's unstylable list, falling back to the shared constant). */
export function removeUnsupportedStyles(component: Component | undefined): void {
  if (!component) {
    return;
  }

  const styles = component.getStyle();
  const unstylable = (component.get('unstylable') as string[] | undefined) ?? UNSUPPORTED_EMAIL_STYLES;

  unstylable.forEach((property) => {
    if (styles[property]) {
      component.removeStyle(property);
    }
  });
}

/** Drops a broken background attribute off the component. */
export function removeBuggyBackgroundAttribute(component: Component | undefined): void {
  if (!component) {
    return;
  }

  const attributes = component.getAttributes();
  delete attributes.background;
  component.setAttributes(attributes);
}

/** Mirrors margin-based alignment into the HTML align attribute for email clients. */
export function updateImageAlignmentCompatibility(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const styles = target.getStyle();
  if (!styles) {
    return;
  }

  if (isCenterAligned(styles)) {
    addAlignAttribute(editor, 'center', target);
  }

  if (isLeftAligned(styles)) {
    addAlignAttribute(editor, 'left', target);
  }

  if (isRightAligned(styles)) {
    addAlignAttribute(editor, 'right', target);
  }
}

/** Re-applies a cell's model vertical-align onto the DOM when they drift apart. */
export function fixVerticalAlignmentVisual(editor: Editor): void {
  const tableCells = editor.getWrapper()?.find(`[data-pmw-type="${EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}"]`);

  tableCells?.forEach((cell) => {
    const isTableParent = cell.parent()?.parent()?.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE);

    if (isTableParent) {
      return;
    }

    const cellEl = cell.getEl();
    const grapesjsVerticalAlign = getStyleValueAsString(cell.getStyle()['vertical-align']);
    const domVerticalAlign = cellEl?.style.verticalAlign;

    if (cellEl && grapesjsVerticalAlign && domVerticalAlign !== grapesjsVerticalAlign) {
      cellEl.style.verticalAlign = grapesjsVerticalAlign;
    }
  });
}

/** Forces the PMW wrapper back to 100% width. */
export function fixWrapperWidth(editor: Editor): void {
  const pmwWrapper = getMainWrapper(editor);
  if (!pmwWrapper) {
    return;
  }

  const wrapperStyles = pmwWrapper.getStyle();
  wrapperStyles.width = '100%';
  pmwWrapper.setStyle(wrapperStyles);
}

/** Rows/tbodies must never be interactable; resets their props when broken templates say otherwise. */
export function fixComponentDefaultProps(component: Component | undefined): void {
  if (!component) {
    return;
  }

  if (component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW) || component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY)) {
    component.set('draggable', false);
    component.set('removable', false);
    component.set('selectable', false);
    component.set('highlightable', false);
    component.set('badgable', false);
    component.set('hoverable', false);
  }
}

/** Silently removes leftover drag-placeholder components. */
export function deleteRedundantComponents(component: Component | undefined): void {
  if (!component) {
    return;
  }

  const type = component.get('type');
  if (type && REDUNDANT_COMPONENT_TYPES.includes(type)) {
    component.remove({silent: true});
  }
}

/** Sentence-cases every text component (the save-error `uppercase-limit` banner CTA), then re-saves. */
export function fixUpperCaseLimit(editor: Editor): void {
  const textComponents = getMainWrapper(editor)?.findType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT);

  if (!textComponents) {
    return;
  }

  textComponents.forEach((component) => {
    component.components(toSentenceCase(component.getEl()?.innerHTML ?? ''));
  });

  void saveEmailTemplateToCache(editor);
}

/** Returns the rendered width normalized against the max mailbox width. */
function getNormalizedWidth(editor: Editor, elementWidth: number): number {
  const documentWidth = editor.Canvas.getElement()?.offsetWidth || DEFAULT_DOCUMENT_WIDTH_PX;
  return Math.round((elementWidth / documentWidth) * MAX_MAILBOX_WIDTH);
}

/** All components under the GrapesJS root wrapper (distinct from getEditableComponents, which walks the PMW wrapper). */
function getAllEditorComponents(editor: Editor, types: string[] = []): Component[] {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return [];
  }

  return getAllComponentsInTree(wrapper, types);
}

/** Reads a StyleProps value as a string; arrays stringify comma-joined, object values map to ''. */
function getStyleValueAsString(value: StyleProps[string] | undefined): string {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(',');
  }

  return '';
}

/** Matches by HTML tag name (not PMW type). */
function isTableTagComponent(component: Component): boolean {
  const tagName = component.get('tagName')?.toLowerCase();
  return tagName === 'table' || tagName === 'tr' || tagName === 'td';
}

function isCenterAligned(styles: StyleProps): boolean {
  return (
    (typeof styles.margin !== 'undefined' && styles.margin === 'auto') ||
    (typeof styles['margin-left'] !== 'undefined' && styles['margin-left'] === 'auto' && typeof styles['margin-right'] !== 'undefined' && styles['margin-right'] === 'auto')
  );
}

function isLeftAligned(styles: StyleProps): boolean {
  return (
    typeof styles['margin-left'] !== 'undefined' &&
    parseInt(styles['margin-left'] as string, 10) === 0 &&
    typeof styles['margin-right'] !== 'undefined' &&
    styles['margin-right'] === 'auto'
  );
}

function isRightAligned(styles: StyleProps): boolean {
  return (
    typeof styles['margin-left'] !== 'undefined' &&
    styles['margin-left'] === 'auto' &&
    typeof styles['margin-right'] !== 'undefined' &&
    parseInt(styles['margin-right'] as string, 10) === 0
  );
}

function toSentenceCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
